import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { signUp, updateUserPassword } from '../../firebase/services';
import { FaEye, FaEyeSlash, FaTrash, FaTimes, FaPlus } from 'react-icons/fa';
import Select from 'react-select';
import './UserManagement.css';
import './ErrorModal.css';

// Error Modal Component
const ErrorModal = ({ error, onClose }) => {
  if (!error) return null;

  return (
    <div className="error-modal-overlay">
      <div className="error-modal">
        <button onClick={onClose} className="error-modal-close">
          <FaTimes />
        </button>
        <h3 className="error-modal-title">Error</h3>
        <p className="error-modal-message">{error}</p>
        <button onClick={onClose} className="error-modal-button">
          Close
        </button>
      </div>
    </div>
  );
};

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: 'user',
    status: 'active',
    assignedLocations: [],
    assignedGroups: []
  });

  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false
  });

  const [passwordError, setPasswordError] = useState('');

  const forceCloseModal = () => {
    setShowModal(false);
    setLoading(false);
    setError(null);
    setNewUser({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      role: 'user',
      status: 'active',
      assignedLocations: [],
      assignedGroups: []
    });
  };

  const fetchUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'users'));
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Ensure status has a default value and is lowercase
        status: (doc.data().status || 'active').toLowerCase()
      }));
      setUsers(usersData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const setInitialAdmin = async () => {
      try {
        const userDocRef = doc(db, 'users', '4fry1QT6PvZA0of2cZeCC1GaxYE3');
        await updateDoc(userDocRef, {
          role: 'admin',
          updatedAt: new Date().toISOString()
        });
        console.log('Successfully set initial admin user');
      } catch (error) {
        console.error('Error setting initial admin:', error);
      }
    };

    setInitialAdmin();
  }, []);

  const handleEdit = (user, field, value) => {
    setEditingField({ userId: user.id, field });
    setEditingValue(value);
  };

  const handleEditSubmit = async (user, field, value) => {
    try {
      const userRef = doc(db, 'users', user.id);
      // Convert status to lowercase before saving
      const valueToSave = field === 'status' ? value.toLowerCase() : value;
      await updateDoc(userRef, { [field]: valueToSave });
      await fetchUsers();
      setEditingField(null);
      setEditingValue('');
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleStatusToggle = async (user) => {
    try {
      const userRef = doc(db, 'users', user.id);
      // Toggle between 'active' and 'inactive' (all lowercase)
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      await updateDoc(userRef, { status: newStatus });
      await fetchUsers();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        await fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: newRole
      });
      console.log(`Updated user ${userId} role to ${newRole}`);
      return true;
    } catch (error) {
      console.error('Error updating user role:', error);
      return false;
    }
  };

  const resetUserRole = async (userId) => {
    try {
      const batch = writeBatch(db);
      
      // Update user role
      const userRef = doc(db, 'users', userId);
      batch.update(userRef, {
        role: 'user'
      });

      await batch.commit();
      console.log('Successfully reset user role to user');
      
      // Refresh the users list
      fetchUsers();
      
      return true;
    } catch (error) {
      console.error('Error resetting user role:', error);
      setError('Failed to reset user role');
      return false;
    }
  };

  const renderEditableCell = (user, field, value) => {
    const isEditing = editingField?.userId === user.id && editingField?.field === field;

    if (field === 'role') {
      return (
        <select
          value={isEditing ? editingValue : value}
          onChange={(e) => handleEditSubmit(user, field, e.target.value)}
          className="role-select"
        >
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="user">User</option>
        </select>
      );
    }

    if (field === 'status') {
      // Ensure value is a string and has a default
      const status = String(value || 'active').toLowerCase();
      return (
        <button
          className={`status-badge ${status}`}
          onClick={() => handleStatusToggle(user)}
        >
          {/* Capitalize first letter for display */}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </button>
      );
    }

    return isEditing ? (
      <input
        type="text"
        value={editingValue}
        onChange={(e) => setEditingValue(e.target.value)}
        onBlur={() => handleEditSubmit(user, field, editingValue)}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            handleEditSubmit(user, field, editingValue);
          }
        }}
        autoFocus
        className="editable-input"
      />
    ) : (
      <span
        className="editable-cell"
        onClick={() => handleEdit(user, field, value)}
      >
        {value}
      </span>
    );
  };

  const fetchLocations = async () => {
    try {
      const locationsRef = collection(db, 'locations');
      const snapshot = await getDocs(locationsRef);
      const locationsList = snapshot.docs
        .map(doc => ({
          value: doc.id,
          label: doc.data().name,
          ...doc.data()
        }))
        .filter(loc => loc.status === 'active');
      
      setLocations(locationsList);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchGroups = async () => {
    try {
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const groupsList = groupsSnapshot.docs
        .map(doc => ({
          value: doc.id,
          label: doc.data().name,
          ...doc.data()
        }))
        .filter(group => group.status === 'active');
      
      setGroups(groupsList);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleLocationChange = async (user, selectedOptions) => {
    try {
      const userRef = doc(db, 'users', user.id);
      const selectedLocations = selectedOptions.map(option => ({
        id: option.value,
        name: option.label
      }));

      // Create update object
      const updateData = {
        assignedLocations: selectedLocations,
        locationId: selectedOptions.length > 0 ? selectedOptions[0].value : null,
        updatedAt: new Date().toISOString()
      };

      console.log('=== Updating User Locations ===', {
        userId: user.id,
        updateData
      });

      await updateDoc(userRef, updateData);
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user locations:', error);
    }
  };

  const handleGroupChange = async (user, selectedOptions) => {
    try {
      const userRef = doc(db, 'users', user.id);
      const selectedGroups = selectedOptions.map(option => ({
        id: option.value,
        name: option.label
      }));

      const updateData = {
        assignedGroups: selectedGroups,
        updatedAt: new Date().toISOString()
      };

      console.log('=== Updating User Groups ===', {
        userId: user.id,
        updateData
      });

      await updateDoc(userRef, updateData);
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user groups:', error);
    }
  };

  const renderLocationSelect = (user) => {
    const userLocations = user.assignedLocations || [];
    const value = userLocations.map(loc => ({
      value: loc.id,
      label: loc.name
    }));

    return (
      <Select
        isMulti
        value={value}
        onChange={(selectedOptions) => handleLocationChange(user, selectedOptions || [])}
        options={locations}
        className="location-select-container"
        classNamePrefix="location-select"
        placeholder="Select locations..."
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: '#2196F3',
            primary75: '#2196F3cc',
            primary50: '#2196F380',
            primary25: '#2196F340',
            danger: '#f44336',
            dangerLight: '#f4433640',
            neutral0: '#011029',
            neutral5: '#011029',
            neutral10: '#011029',
            neutral20: 'rgba(255, 255, 255, 0.2)',
            neutral30: 'rgba(255, 255, 255, 0.3)',
            neutral40: 'rgba(255, 255, 255, 0.4)',
            neutral50: 'rgba(255, 255, 255, 0.5)',
            neutral60: 'rgba(255, 255, 255, 0.6)',
            neutral70: 'rgba(255, 255, 255, 0.7)',
            neutral80: 'rgba(255, 255, 255, 0.8)',
            neutral90: 'rgba(255, 255, 255, 0.9)'
          }
        })}
        styles={{
          control: (provided) => ({
            ...provided,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': {
              borderColor: '#2196F3'
            }
          }),
          menu: (provided) => ({
            ...provided,
            backgroundColor: '#011029',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
            color: 'white',
            '&:active': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            }
          }),
          multiValue: (provided) => ({
            ...provided,
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }),
          multiValueLabel: (provided) => ({
            ...provided,
            color: 'white'
          }),
          multiValueRemove: (provided) => ({
            ...provided,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.2)',
              color: '#f44336'
            }
          }),
          input: (provided) => ({
            ...provided,
            color: 'white'
          })
        }}
      />
    );
  };

  const renderGroupSelect = (user) => {
    const userGroups = user.assignedGroups || [];
    const value = userGroups.map(group => ({
      value: group.id,
      label: group.name
    }));

    return (
      <Select
        isMulti
        value={value}
        onChange={(selectedOptions) => handleGroupChange(user, selectedOptions || [])}
        options={groups}
        className="group-select-container"
        classNamePrefix="group-select"
        placeholder="Select groups..."
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: '#2196F3',
            primary75: '#2196F3cc',
            primary50: '#2196F380',
            primary25: '#2196F340',
            danger: '#f44336',
            dangerLight: '#f4433640',
            neutral0: '#011029',
            neutral5: '#011029',
            neutral10: '#011029',
            neutral20: 'rgba(255, 255, 255, 0.2)',
            neutral30: 'rgba(255, 255, 255, 0.3)',
            neutral40: 'rgba(255, 255, 255, 0.4)',
            neutral50: 'rgba(255, 255, 255, 0.5)',
            neutral60: 'rgba(255, 255, 255, 0.6)',
            neutral70: 'rgba(255, 255, 255, 0.7)',
            neutral80: 'rgba(255, 255, 255, 0.8)',
            neutral90: 'rgba(255, 255, 255, 0.9)'
          }
        })}
        styles={{
          control: (provided) => ({
            ...provided,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': {
              borderColor: '#2196F3'
            }
          }),
          menu: (provided) => ({
            ...provided,
            backgroundColor: '#011029',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
            color: 'white',
            '&:active': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            }
          }),
          multiValue: (provided) => ({
            ...provided,
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }),
          multiValueLabel: (provided) => ({
            ...provided,
            color: 'white'
          }),
          multiValueRemove: (provided) => ({
            ...provided,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.2)',
              color: '#f44336'
            }
          }),
          input: (provided) => ({
            ...provided,
            color: 'white'
          })
        }}
      />
    );
  };

  const handleAddUser = async (e) => {
    e.preventDefault();

    if (!newUser.email || !newUser.password || !newUser.confirmPassword || 
        !newUser.firstName || !newUser.lastName || !newUser.role) {
      setError('All fields are required');
      return;
    }

    if (newUser.password !== newUser.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!newUser.assignedLocations || newUser.assignedLocations.length === 0) {
      setError('Please select at least one location');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Create the new user
      await signUp(newUser.email.trim(), newUser.password, {
        firstName: newUser.firstName.trim(),
        lastName: newUser.lastName.trim(),
        role: newUser.role,
        status: newUser.status,
        assignedLocations: newUser.assignedLocations,
        assignedGroups: newUser.assignedGroups,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      console.log('User created successfully');
      setShowModal(false);
      setNewUser({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        role: 'User',
        status: 'Active',
        assignedLocations: [],
        assignedGroups: []
      });
      await fetchUsers(); // Refresh the users list
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.message || 'Failed to create user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordModalSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      console.error('No user selected');
      return;
    }

    setLoading(true);
    try {
      const result = await updateUserPassword(selectedUser.id);
      console.log('Password reset initiated successfully');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setSelectedUser(null);
      // Show success message to admin
      alert(result.message);
    } catch (error) {
      console.error('Error initiating password reset:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-management">
      <ErrorModal error={error} onClose={() => setError(null)} />
      <div className="user-controls">
        <button 
          onClick={() => {
            setShowModal(true);
            setError(null);
            setNewUser({
              email: '',
              password: '',
              confirmPassword: '',
              firstName: '',
              lastName: '',
              role: 'user',
              status: 'active',
              assignedLocations: [],
              assignedGroups: []
            });
          }} 
          className="add-btn"
        >
          <FaPlus /> Add New User
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Groups</th>
              <th>Locations</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{renderEditableCell(user, 'firstName', user.firstName)}</td>
                <td>{renderEditableCell(user, 'lastName', user.lastName)}</td>
                <td>{renderEditableCell(user, 'email', user.email)}</td>
                <td>{renderEditableCell(user, 'role', user.role)}</td>
                <td className="group-cell">{renderGroupSelect(user)}</td>
                <td className="location-cell">{renderLocationSelect(user)}</td>
                <td>{renderEditableCell(user, 'status', user.status)}</td>
                <td className="actions-cell">
                  <button
                    onClick={() => {
                      setSelectedUser(user);
                      setShowPasswordModal(true);
                    }}
                    className="password-change-btn"
                  >
                    Change Password
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    className="delete-btn"
                    title="Delete User"
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New User</h3>
              <button 
                onClick={() => {
                  setShowModal(false);
                  setError(null);
                  setNewUser({
                    email: '',
                    password: '',
                    confirmPassword: '',
                    firstName: '',
                    lastName: '',
                    role: 'user',
                    status: 'active',
                    assignedLocations: [],
                    assignedGroups: []
                  });
                }} 
                className="close-button"
                type="button"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAddUser}>
              {error && (
                <div style={{
                  background: 'rgba(220, 38, 38, 0.1)',
                  border: '1px solid #dc2626',
                  color: '#dc2626',
                  padding: '12px',
                  margin: '0 0 16px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  {error}
                </div>
              )}
              <div className="form-group">
                <label>First Name:</label>
                <input
                  type="text"
                  value={newUser.firstName}
                  onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name:</label>
                <input
                  type="text"
                  value={newUser.lastName}
                  onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password:</label>
                <div className="password-input-container">
                  <input
                    type={showPassword.password ? "text" : "password"}
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    required
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword({...showPassword, password: !showPassword.password})}
                  >
                    {showPassword.password ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Confirm Password:</label>
                <div className="password-input-container">
                  <input
                    type={showPassword.confirmPassword ? "text" : "password"}
                    value={newUser.confirmPassword}
                    onChange={(e) => setNewUser({...newUser, confirmPassword: e.target.value})}
                    required
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword({...showPassword, confirmPassword: !showPassword.confirmPassword})}
                  >
                    {showPassword.confirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Role:</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                >
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status:</label>
                <select
                  value={newUser.status}
                  onChange={(e) => setNewUser({...newUser, status: e.target.value})}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="form-group">
                <label>Assigned Location:</label>
                <Select
                  isMulti
                  value={newUser.assignedLocations}
                  onChange={(selectedOptions) => setNewUser({...newUser, assignedLocations: selectedOptions})}
                  options={locations}
                  className="location-select-container"
                  classNamePrefix="location-select"
                  placeholder="Select locations..."
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary: '#2196F3',
                      primary75: '#2196F3cc',
                      primary50: '#2196F380',
                      primary25: '#2196F340',
                      danger: '#f44336',
                      dangerLight: '#f4433640',
                      neutral0: '#011029',
                      neutral5: '#011029',
                      neutral10: '#011029',
                      neutral20: 'rgba(255, 255, 255, 0.2)',
                      neutral30: 'rgba(255, 255, 255, 0.3)',
                      neutral40: 'rgba(255, 255, 255, 0.4)',
                      neutral50: 'rgba(255, 255, 255, 0.5)',
                      neutral60: 'rgba(255, 255, 255, 0.6)',
                      neutral70: 'rgba(255, 255, 255, 0.7)',
                      neutral80: 'rgba(255, 255, 255, 0.8)',
                      neutral90: 'rgba(255, 255, 255, 0.9)'
                    }
                  })}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        borderColor: '#2196F3'
                      }
                    }),
                    menu: (provided) => ({
                      ...provided,
                      backgroundColor: '#011029',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      color: 'white',
                      '&:active': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)'
                      }
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: 'white'
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.2)',
                        color: '#f44336'
                      }
                    }),
                    input: (provided) => ({
                      ...provided,
                      color: 'white'
                    })
                  }}
                />
              </div>
              <div className="form-group">
                <label>Assigned Group:</label>
                <Select
                  isMulti
                  value={newUser.assignedGroups}
                  onChange={(selectedOptions) => setNewUser({...newUser, assignedGroups: selectedOptions})}
                  options={groups}
                  className="group-select-container"
                  classNamePrefix="group-select"
                  placeholder="Select groups..."
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary: '#2196F3',
                      primary75: '#2196F3cc',
                      primary50: '#2196F380',
                      primary25: '#2196F340',
                      danger: '#f44336',
                      dangerLight: '#f4433640',
                      neutral0: '#011029',
                      neutral5: '#011029',
                      neutral10: '#011029',
                      neutral20: 'rgba(255, 255, 255, 0.2)',
                      neutral30: 'rgba(255, 255, 255, 0.3)',
                      neutral40: 'rgba(255, 255, 255, 0.4)',
                      neutral50: 'rgba(255, 255, 255, 0.5)',
                      neutral60: 'rgba(255, 255, 255, 0.6)',
                      neutral70: 'rgba(255, 255, 255, 0.7)',
                      neutral80: 'rgba(255, 255, 255, 0.8)',
                      neutral90: 'rgba(255, 255, 255, 0.9)'
                    }
                  })}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        borderColor: '#2196F3'
                      }
                    }),
                    menu: (provided) => ({
                      ...provided,
                      backgroundColor: '#011029',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      color: 'white',
                      '&:active': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)'
                      }
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: 'white'
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.2)',
                        color: '#f44336'
                      }
                    }),
                    input: (provided) => ({
                      ...provided,
                      color: 'white'
                    })
                  }}
                />
              </div>
              <div className="modal-buttons">
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create User'}
                </button>
                <button 
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setError(null);
                    setNewUser({
                      email: '',
                      password: '',
                      confirmPassword: '',
                      firstName: '',
                      lastName: '',
                      role: 'user',
                      status: 'active',
                      assignedLocations: [],
                      assignedGroups: []
                    });
                  }}
                  className="cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Reset Password</h2>
            </div>
            <form onSubmit={handlePasswordModalSubmit}>
              <div className="form-group">
                <p>Send a password reset email to: <strong>{selectedUser.email}</strong></p>
                <p className="info-text">The user will receive an email with instructions to reset their password.</p>
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="modal-buttons">
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Email'}
                </button>
                <button 
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setError(null);
                    setSelectedUser(null);
                  }}
                  className="cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <div className="user-actions">
        <button 
          onClick={() => resetUserRole('8AgH6DR3YJYsa12qx0vFlvPjDXH2')}
          className="reset-role-button"
        >
          Reset Test User Role
        </button>
      </div>
    </div>
  );
}

export default UserManagement;